// Données ultra denses des algorithmes par plateforme
// Priorité maximale: IG, TikTok, YouTube Shorts (4 phases full)
// Secondaire: Threads, X, Snap, Clapper (2-3 phases)

export const ALGORITHMS = {
  instagram: {
    slug: "instagram",
    name: "Instagram",
    icon: "Instagram",
    color: "#E1306C",
    tagline: "L'algorithme cascade. Chaque palier franchi = niveau de reach supérieur.",
    overview: "IG est une cascade de filtres. Cold → Warm → Explore. Watchtime + saves > likes. Shares en DM = signal nucléaire.",
    phases: [
      {
        id: "cold",
        title: "Phase 1 — Audience Cold",
        range: "0-200 vues",
        intro: "IG montre ton post à 5-10% de tes followers actifs comme test silencieux. Si signal vert dans les 30 premières minutes → palier suivant.",
        stats: [
          { label: "Watchtime cible", value: "≥ 70%" },
          { label: "Save rate cible", value: "≥ 4%" },
          { label: "Engagement rate", value: "≥ 6%" },
          { label: "Fenêtre critique", value: "30 min" },
        ],
        actions: [
          "Poster à l'heure de pic d'audience (vérifie tes Insights)",
          "Demander à 5-10 contacts proches de voir le post dans les 5 minutes",
          "Story du Reel 1h après publication pour booster watchtime indirect",
          "Ne JAMAIS éditer la caption les 30 premières minutes (reset signal)",
          "Répondre aux 5 premiers commentaires en moins de 10 minutes",
        ],
        checklist: [
          "Hook visuel dans les 0-1 secondes",
          "Sous-titres dynamiques activés",
          "Caption avec hook + CTA invisible",
          "Hashtags : 3 niche + 2 large + 0 spammy",
          "Audio trending si applicable (fenêtre 7-14j)",
          "Cover/thumbnail propre et lisible",
          "Localisation activée si pertinent",
        ],
        kpis: [
          { label: "Vues 1h", value: "≥ 30% de ta moyenne 7j" },
          { label: "Saves 1h", value: "≥ 5 minimum" },
          { label: "Skip rate", value: "< 50%" },
          { label: "Watchtime moyen", value: "≥ 70%" },
        ],
        errors: [
          "Poster un Reel avec watermark TikTok/CapCut → -40% reach systémique",
          "Hashtags spammy (#followforfollow, #viral) → -60% reach",
          "Caption trop longue sans hook → skip avant lecture",
          "Éditer la caption dans la première heure → reset du signal",
          "Désactiver les commentaires → tag 'low quality'",
        ],
        proTips: [
          "Le 'Power Comment' : poste un commentaire texte de 50+ caractères en réponse au premier commentaire. Boost engagement signal.",
          "Save personnel via compte secondaire dans les 3 minutes : signal save x3 vs like.",
          "Repartage en story d'un commentaire intéressant : push secondaire à toute ton audience.",
        ],
        examples: [
          "Hook: 'Personne ne te dira ça mais...' → curiosity gap, +20% watchtime moyen",
          "Hook visuel: zoom sur un produit + texte qui apparaît → stop scroll mécanique",
        ],
        warmupLink: "Compte neuf : ne pas poster avant J7 minimum. Phase Cold suppose un compte chauffé."
      },
      {
        id: "engagement",
        title: "Phase 2 — Push Audience Tiède",
        range: "200 - 5K vues",
        intro: "IG élargit à audience similaire. Engagement rate ≥ 4% obligatoire pour passer en Phase 3. C'est le moment où le post prend vie ou meurt.",
        stats: [
          { label: "Engagement rate", value: "≥ 4%" },
          { label: "Saves cumulés", value: "≥ 2% des vues" },
          { label: "Shares DM", value: "≥ 1% des vues" },
          { label: "Fenêtre", value: "1-6h post-publication" },
        ],
        actions: [
          "Répondre à TOUS les commentaires dans les 30 minutes",
          "Liker chaque commentaire → boost taux de réponse retour",
          "Story 'derrière les coulisses' du Reel pour push secondaire",
          "Cross-post sur Threads et Facebook avec adaptation hook",
          "Tag 1-2 comptes pertinents si valeur réelle (pas spam)",
        ],
        checklist: [
          "Tous les commentaires répondus < 30min",
          "Story de promotion lancée",
          "Post épinglé en story 24h",
          "Cross-post effectué",
          "Suivi KPIs en temps réel (Insights)",
        ],
        kpis: [
          { label: "Comments rate", value: "≥ 0.8%" },
          { label: "Shares rate", value: "≥ 1%" },
          { label: "Profile visits", value: "≥ 2% des vues" },
          { label: "Follows from post", value: "≥ 0.3% des vues" },
        ],
        errors: [
          "Ne pas répondre aux commentaires → algo détecte 'créateur passif'",
          "Trop de hashtags rajoutés en commentaire → spam signal",
          "Multipler les CTA agressifs en commentaires → friction",
          "Cross-post sans adaptation du hook → faible perf sur autres plateformes",
        ],
        proTips: [
          "Les 'comment-to-DM' boosts : invite les viewers à commenter un mot-clé pour recevoir du contenu en DM. Multiplie commentaires + DMs (deux signaux).",
          "Réponses longues + question ouverte → crée chaîne de commentaires en cascade.",
        ],
        examples: [
          "Réponse type : 'Excellent point — tu fais comment toi pour [X] ?' → relance commentaire",
        ],
        warmupLink: "Compte chauffé peut absorber +30% d'actions en Phase 2 vs compte tiède."
      },
      {
        id: "fidelisation",
        title: "Phase 3 — Explore / Reels Feed",
        range: "5K - 100K+ vues",
        intro: "Push à audience cold globale via Explore et Reels Feed. Watchtime + shares = nitro. C'est ici que les reels passent à 6 chiffres.",
        stats: [
          { label: "Watchtime moy.", value: "≥ 80%" },
          { label: "Re-watch rate", value: "≥ 15%" },
          { label: "Share rate", value: "≥ 2%" },
          { label: "Fenêtre", value: "6-72h" },
        ],
        actions: [
          "Doubler les réponses commentaires (chaque heure pendant 6h)",
          "Partager le Reel dans les Closes Friends → boost engagement qualifié",
          "Préparer un follow-up content qui capitalise sur la viralité",
          "Activer les notifications pour ne rien rater",
          "DM les nouveaux followers qualifiés avec offre de valeur",
        ],
        checklist: [
          "Loop visuel parfait (dernière frame ≈ première frame)",
          "Sous-titres impeccables (correction errors)",
          "CTA pour follow ou content suivant",
          "Bio du compte optimisée pour la conversion",
          "Lien en bio actif et trackable",
        ],
        kpis: [
          { label: "Vues totales 24h", value: "≥ 5x ta moyenne" },
          { label: "Nouveaux followers", value: "≥ 1% des vues" },
          { label: "Profile visits", value: "≥ 5% des vues" },
        ],
        errors: [
          "Repost trop rapide d'un autre Reel → cannibalise ton viral en cours",
          "Changer de niche pendant la viralité → audience perdue",
          "Stories sans rapport avec le Reel viral → break du momentum",
        ],
        proTips: [
          "Profil-trap : le viewer qui clique sur ton profil DOIT trouver 3 autres reels similaires en haut. Réorganise ton feed pour capitaliser.",
          "Pinned posts stratégiques : épingle 3 reels qui amplifient le narratif du viral en cours.",
          "Lance un 'partie 2' dans les 24-48h pour capitaliser sur la rétention.",
        ],
        examples: [
          "Bio optimisée : @handle / 1 ligne value prop / CTA ('Reçois X en DM en commentant Y')",
        ],
        warmupLink: "Compte avec historique stable absorbe la viralité sans flag. Compte récemment chauffé peut être limité."
      },
      {
        id: "scaling",
        title: "Phase 4 — Scaling Post-Viral",
        range: "100K+ vues",
        intro: "Le piège du one-hit wonder. Beaucoup explosent une fois puis chutent. Le scaling consiste à transformer le viral en croissance durable.",
        stats: [
          { label: "Retention rate vs avant", value: "≥ 90%" },
          { label: "Email/DM list growth", value: "Track quotidien" },
          { label: "Engagement post-viral", value: "≥ 70% de la moyenne" },
        ],
        actions: [
          "Lancer série de 3-5 contenus dans la même veine que le viral",
          "Profile makeover : highlights, bio, lien ajustés pour conversion",
          "DM auto-reply de remerciement avec valeur (lien gratuit, etc.)",
          "Récupérer les emails / leads qualifiés via outil",
          "Préparer le next big content : ne pas baisser la qualité",
        ],
        checklist: [
          "Système de capture audience activé (lien bio, lead magnet)",
          "Welcome DM template prêt",
          "Calendrier de publication post-viral verrouillé",
          "Analyse des viewers du viral (démographie)",
          "Branding visuel renforcé sur next posts",
        ],
        kpis: [
          { label: "Conversion view → follower", value: "≥ 1%" },
          { label: "Conversion follower → email", value: "≥ 5%" },
          { label: "Maintien vues 7j post-viral", value: "≥ 60% de la moyenne d'avant" },
        ],
        errors: [
          "Spammer du contenu derrière le viral → désaboo massif",
          "Changer de niche post-viral → perte d'audience",
          "Négliger les DMs entrants → opportunités cramées",
          "Ne pas capter les emails → audience volatile",
        ],
        proTips: [
          "Le 'second wave' play : ne pas reposter le viral mais une variation 7-14j après. Souvent fait de meilleurs scores.",
          "Pinned posts post-viral : épingle 3 reels qui poursuivent le narratif → même les viewers cold du viral consomment plus.",
          "Capture par DM : 'Réponds VIRAL pour recevoir le système complet' → email list explose.",
        ],
        examples: [
          "Compte qui passe de 50K à 500K abonnés en 30j post-viral grâce à 5 reels follow-up cohérents.",
        ],
        warmupLink: "Le scaling demande un compte 100% propre. Aucun ban risk toléré post-viral."
      }
    ]
  },

  tiktok: {
    slug: "tiktok",
    name: "TikTok",
    icon: "Music2",
    color: "#FF0050",
    tagline: "La machine virale la plus prédictible. 0 → 1M en une vidéo possible.",
    overview: "TikTok pousse par paliers mathématiques. 200 → 1K → 10K → 100K → FYP global. Watchtime + completion rate sont les rois.",
    phases: [
      {
        id: "cold",
        title: "Phase 1 — Test Cold",
        range: "0 - 500 vues",
        intro: "TikTok teste systématiquement chaque vidéo sur 200-500 cold viewers, peu importe ton compte. Si signal vert → palier suivant immédiat.",
        stats: [
          { label: "Watchtime cible", value: "≥ 60%" },
          { label: "Completion rate", value: "≥ 30%" },
          { label: "Engagement rate", value: "≥ 8%" },
          { label: "Fenêtre", value: "30 min - 2h" },
        ],
        actions: [
          "Hook visuel + sonore dans les 0-1 secondes",
          "Loop invisible : dernière frame = première frame",
          "Sous-titres TIKTOK natifs (pas brûlés en post-prod)",
          "Caption courte + 1-2 hashtags maximum",
          "Pas de watermark, pas d'EXPORT visible",
        ],
        checklist: [
          "Vidéo 8-15 secondes max pour Phase 1",
          "Format 9:16 plein écran, pas de bandes noires",
          "Son trending (vérifier dans Sounds, fenêtre <14j)",
          "Texte à l'écran qui complète le hook",
          "Mouvement caméra ou cuts toutes les 1-2s",
        ],
        kpis: [
          { label: "Watchtime moyen", value: "≥ 60%" },
          { label: "Completion 100%", value: "≥ 25%" },
          { label: "Likes/views", value: "≥ 8%" },
          { label: "Saves", value: "≥ 1%" },
        ],
        errors: [
          "Watermark TikTok/CapCut visible → ban algo direct",
          "Vidéo trop longue dès le test cold (>15s)",
          "Pas de loop → drop dramatique de re-watch",
          "Hook trop tardif (>1.5s) → 70% skip immédiat",
          "Trop de texte qui obscurcit la vidéo",
        ],
        proTips: [
          "Le 'invisible cut' : un blackframe de 1 frame entre 2 plans → ré-attire l'œil sans le viewer s'en rende compte.",
          "Hook auditif : musique qui change brutalement à 0.5s + texte qui apparaît = pattern interrupt sonore.",
          "Test à plusieurs heures : poste la même vidéo (variations cover) à différentes heures pour trouver ton sweet spot.",
        ],
        examples: [
          "Hook visuel : main qui apparaît avec un objet + texte 'Si tu vois ça en 2026, t'as 3 secondes pour comprendre'",
        ],
        warmupLink: "Compte neuf : commencer par 2-3 vidéos courtes pendant 7j sans CTA agressif."
      },
      {
        id: "engagement",
        title: "Phase 2 — Push Audience Similaire",
        range: "1K - 10K vues",
        intro: "TikTok pousse à audience similaire géographique. Shares = clé pour palier suivant. Comments en cascade = signal hot.",
        stats: [
          { label: "Share rate", value: "≥ 1.5%" },
          { label: "Comments rate", value: "≥ 1%" },
          { label: "Watchtime maintenu", value: "≥ 55%" },
          { label: "Re-watch", value: "≥ 20%" },
        ],
        actions: [
          "Répondre aux commentaires en VIDÉO (réponse vidéo TikTok)",
          "Liker tous les commentaires des 2 premières heures",
          "Stitch ou Duet stratégique d'un commentaire viral",
          "Pinner les meilleurs commentaires (boost engagement)",
          "Suivi KPI en temps réel via TikTok Analytics",
        ],
        checklist: [
          "Top 5 commentaires épinglés",
          "Réponses commentaires faites < 1h",
          "Au moins 1 réponse vidéo lancée",
          "Cross-post Reels IG dans les 30min",
          "Story TikTok du contenu",
        ],
        kpis: [
          { label: "Vues 6h", value: "≥ 5x cold" },
          { label: "Profile views", value: "≥ 3% des vues" },
          { label: "Follows", value: "≥ 0.8% des vues" },
        ],
        errors: [
          "Ne pas exploiter les comments en vidéo → algo loupé",
          "Réponses textuelles plates → faible push secondaire",
          "Trop de pinned commentaires (>3) → confus pour viewers",
        ],
        proTips: [
          "Comment-to-comment battle : invite les viewers à débattre dans les commentaires → conversation = signal hot.",
          "Reply vidéo en 'partie 2' : ta réponse vidéo devient un viral à part qui ramène les viewers vers le viral original.",
        ],
        examples: [
          "Pinned comment : 'Vous voulez la partie 2 ?' → 800 réponses → push palier",
        ],
        warmupLink: "Compte chauffé peut absorber Phase 2 sans risque. Compte spam → souvent stuck en Phase 1."
      },
      {
        id: "fidelisation",
        title: "Phase 3 — FYP Géographique Élargi",
        range: "10K - 100K vues",
        intro: "TikTok élargit géographiquement. C'est le palier où la majorité des viraux passent à 6 chiffres. Loop + retention = obligatoire.",
        stats: [
          { label: "Loop completion", value: "≥ 40%" },
          { label: "Watchtime", value: "≥ 70%" },
          { label: "Profile clicks", value: "≥ 4%" },
          { label: "Fenêtre push", value: "12-72h" },
        ],
        actions: [
          "Maintenir activité commentaires sur le viral",
          "Poster un follow-up vidéo dans les 24h (capitalise momentum)",
          "Update bio + lien pour conversion",
          "Activer DM auto-reply pour nouveaux followers",
          "Préparer le next viral en analysant ce qui a marché",
        ],
        checklist: [
          "Bio optimisée avec CTA clair",
          "Pinned 3 vidéos best-of",
          "Lien bio fonctionne et tracké",
          "DM template prêt",
          "Welcome message audio pour grosse audience",
        ],
        kpis: [
          { label: "Vues 24h", value: "≥ 50K" },
          { label: "Followers nets", value: "≥ 1500" },
          { label: "Profile visit rate", value: "≥ 5%" },
        ],
        errors: [
          "Profil sans pinned posts cohérents → conversion view→follow plomb",
          "Bio générique → 0 conversion",
          "Spam de vidéos derrière → cannibalisation",
        ],
        proTips: [
          "Le 'profile trap' : pinned posts forment un narratif de 3 vidéos qui complètent le viral. Le viewer cold consomme 4 vidéos en 5 minutes.",
          "Reply vidéo aux commentaires les plus likés : boost secondaire et engage la nouvelle audience.",
        ],
        examples: [],
        warmupLink: "Compte stable absorbe Phase 3 sans flag. Compte au comportement erratique = palier max."
      },
      {
        id: "scaling",
        title: "Phase 4 — FYP Global / Scaling",
        range: "100K+ vues",
        intro: "Le palier 1M+ vues est un autre jeu. Il faut transformer le viral en machine de croissance durable, sinon retour à zéro.",
        stats: [
          { label: "Conversion vue→follow", value: "≥ 1.5%" },
          { label: "Email capture", value: "≥ 3% des followers" },
          { label: "Maintien J7 post-viral", value: "≥ 70%" },
        ],
        actions: [
          "Série de 5-7 vidéos dans la même veine que le viral",
          "Bio + lien optimisés pour leadgen",
          "DM auto-reply avec lead magnet",
          "Track les nouvelles audiences (démographie)",
          "Préparer le 'big follow-up' à publier J5-J7",
        ],
        checklist: [
          "Lead magnet créé et accessible",
          "DM template avec valeur immédiate",
          "Calendrier J+1 à J+14 verrouillé",
          "Analyse audience faite",
        ],
        kpis: [
          { label: "Followers post-viral 30j", value: "≥ 2x" },
          { label: "Engagement rate maintenu", value: "≥ 6%" },
          { label: "Email list", value: "Croissance continue" },
        ],
        errors: [
          "Sortir de la niche post-viral → audience perdue",
          "Vidéos low effort derrière → désaboo massif",
          "Pas de capture email → tout repose sur l'algo",
        ],
        proTips: [
          "Le 'second viral' technique : variation du viral à J7-J10 souvent fait des scores supérieurs.",
          "Capture massive : DM keyword qui livre lead magnet → 1M vues = 5K-10K emails capturés.",
        ],
        examples: [
          "Compte fitness à 50K → viral 2M vues → série 7 vidéos → 250K abos en 30j",
        ],
        warmupLink: "Aucun risque toléré : compte 100% propre obligatoire pendant scaling."
      }
    ]
  },

  youtube: {
    slug: "youtube",
    name: "YouTube Shorts",
    icon: "Youtube",
    color: "#FF0000",
    tagline: "Watchtime le plus long de toutes les plateformes. Audience plus mature, monétisation directe.",
    overview: "YouTube Shorts a une logique différente : audience 25-45 plus mature, watchtime moyen plus haut (60% vs 35% sur TT), RPM monétisable.",
    phases: [
      {
        id: "cold",
        title: "Phase 1 — Test Cold YT",
        range: "0 - 1K vues",
        intro: "YouTube teste sur 100-500 viewers ciblés en fonction de l'historique du compte. SEO du titre + thumbnail comptent même sur Shorts.",
        stats: [
          { label: "Watchtime cible", value: "≥ 65%" },
          { label: "Click-through (titre)", value: "≥ 4%" },
          { label: "Subscriber rate", value: "≥ 0.5%" },
        ],
        actions: [
          "Titre SEO clair (mots-clés niche)",
          "Description avec lien vers vidéo longue (boost watchtime canal)",
          "Hashtag #Shorts en description",
          "Hook 0-2s qui annonce ce que la vidéo va prouver",
          "Cross-post depuis TT/IG SANS watermark",
        ],
        checklist: [
          "Titre 60 caractères max, niche claire",
          "Description avec 2-3 mots-clés SEO",
          "Thumbnail propre (même sur Short)",
          "End-screen qui pousse vers vidéo longue",
          "Tags niche dans description",
        ],
        kpis: [
          { label: "Average view duration", value: "≥ 65%" },
          { label: "Likes ratio", value: "≥ 5%" },
          { label: "Subs from short", value: "≥ 0.5%" },
        ],
        errors: [
          "Watermark TikTok visible → -50% push",
          "Titre clickbait sans payoff → audience retention plomb",
          "Pas de description → SEO loupé",
        ],
        proTips: [
          "Le Shorts→Long redirect : Short qui pose une question + end-screen vers la vidéo longue qui répond. Boost watchtime canal.",
          "Description SEO mini : 3 lignes max avec mots-clés niche + lien autres shorts.",
        ],
        examples: [],
        warmupLink: "Canal neuf : poster 5-10 shorts avant la première vidéo longue."
      },
      {
        id: "engagement",
        title: "Phase 2 — Push Subscribers",
        range: "1K - 50K vues",
        intro: "YouTube push aux subscribers de comptes similaires. Sub from Short = signal premium pour palier suivant.",
        stats: [
          { label: "Subscriber rate", value: "≥ 1%" },
          { label: "Watchtime", value: "≥ 70%" },
          { label: "Comments per 1K", value: "≥ 5" },
        ],
        actions: [
          "Répondre aux commentaires (top 10) avec hearts",
          "Pinned commentaire qui pousse vers la vidéo longue associée",
          "Cross-promote dans la community tab",
          "Préparer suite à publier J+2",
        ],
        checklist: [
          "Top commentaire épinglé avec CTA",
          "Réponses commentaires < 6h",
          "Community post de promotion",
          "Sub button visible mentionné dans hook ou outro",
        ],
        kpis: [
          { label: "Subs gain 24h", value: "≥ 2% des vues" },
          { label: "Watch on channel page", value: "Augmente" },
        ],
        errors: [
          "Pas de mention 'subscribe' → conversion plomb",
          "Sortie de niche → désabo immédiat",
        ],
        proTips: [
          "Pinned comment = pré-vente : 'Si t'aimes ça, abonne-toi pour la partie 2 cette semaine.'",
        ],
        examples: [],
        warmupLink: "Canal régulier (>3 shorts/semaine pendant 14j) absorbe Phase 2 sans flag."
      },
      {
        id: "fidelisation",
        title: "Phase 3 — Browse Features",
        range: "50K - 500K vues",
        intro: "YouTube push dans Browse Features (homepage des cold viewers). Watchtime canal global = clé.",
        stats: [
          { label: "Channel watchtime", value: "Augmente" },
          { label: "Average view duration", value: "≥ 80%" },
          { label: "Suggested video clicks", value: "≥ 3%" },
        ],
        actions: [
          "Lancer série de 3-5 shorts cohérents (même thumbnail style)",
          "Vidéo longue qui capitalise sur le viral en cours",
          "Premier 'second wave' short à J5-J7",
        ],
        checklist: [
          "Visual cohérence sur 5 shorts",
          "Vidéo longue prévue dans la semaine",
          "Channel art mis à jour si pertinent",
        ],
        kpis: [
          { label: "Subs gagnés 7j", value: "≥ 5K" },
          { label: "RPM Shorts", value: "≥ $0.10" },
        ],
        errors: [
          "Vidéo longue absente après viral → opportunité monétisation perdue",
          "Style visuel incohérent → conversion plomb",
        ],
        proTips: [
          "Le 'channel funnel' : 1 viral short → community post → vidéo longue → membership → produit. Capture la valeur à chaque étape.",
        ],
        examples: [],
        warmupLink: "Canal stable obligatoire."
      },
      {
        id: "scaling",
        title: "Phase 4 — Long-form Funnel",
        range: "500K+ vues / monétisation",
        intro: "Le scaling YouTube passe par la transition Shorts → Vidéos longues monétisées. C'est ici que les vrais $ rentrent.",
        stats: [
          { label: "RPM longue", value: "$5-$30" },
          { label: "Conversion Short→Long", value: "≥ 2%" },
          { label: "Membership conversions", value: "≥ 0.5%" },
        ],
        actions: [
          "Vidéo longue de 8-15 min qui prolonge le viral",
          "Membership ou Patreon launch si audience qualifiée",
          "Sponsorships (réception offres)",
          "Scaling cross-plateformes",
        ],
        checklist: [
          "Vidéo longue publiée et SEO optimisée",
          "Sponsor pitch deck prêt",
          "Membership activé (si éligible)",
        ],
        kpis: [
          { label: "Revenu mensuel YouTube", value: "Croissance continue" },
        ],
        errors: [
          "Rester en Shorts only → plafond revenu",
          "Pas de stratégie longue → AdSense plomb",
        ],
        proTips: [
          "Une vidéo longue par mois minimum = +300% RPM canal en moyenne.",
        ],
        examples: [],
        warmupLink: "Scaling YouTube = canal sans strikes ni warnings."
      }
    ]
  },

  threads: {
    slug: "threads",
    name: "Threads",
    icon: "AtSign",
    color: "#000000",
    tagline: "Le réseau gratuit. 0 abo peut faire 100K vues facilement.",
    overview: "Threads en 2026 = ce que Twitter était en 2010. L'algo favorise massivement les nouveaux comptes et les hot takes courts.",
    phases: [
      {
        id: "cold",
        title: "Phase 1 — Push Initial",
        range: "0 - 5K vues",
        intro: "Threads montre tes posts à des cold viewers basés sur ton historique IG. Format court, opinion tranchée = explosion.",
        stats: [
          { label: "Engagement rate", value: "≥ 10%" },
          { label: "Reply rate", value: "≥ 2%" },
          { label: "Repost rate", value: "≥ 1%" },
        ],
        actions: [
          "Posts ultra courts (1-2 phrases)",
          "Hot take polarisant",
          "Question rhétorique punchy",
          "Quote-thread de soi-même pour continuation",
          "Réponse first 30min sous 3-5 gros posts",
        ],
        checklist: [
          "Pas plus de 280 caractères pour le post principal",
          "Hashtag 0-1 max",
          "Mentionner un compte populaire si pertinent",
          "Post entre 7-9h ou 19-22h (heures de pic)",
        ],
        kpis: [
          { label: "Replies", value: "≥ 20" },
          { label: "Likes", value: "≥ 100" },
          { label: "Reposts", value: "≥ 10" },
        ],
        errors: [
          "Posts trop longs → skip",
          "Liens externes au premier post → -70% reach",
          "Pas de réponses sous gros comptes → 0 traffic gratuit",
        ],
        proTips: [
          "La technique 'reply first' : commenter sous un gros post DANS LES 30 MINUTES = ton commentaire est vu par tous les viewers du gros post. 10K-100K vues gratuites.",
          "Quote-thread soi-même 24h après = relance du post original.",
        ],
        examples: [
          "Hot take : 'En 2026, les créateurs qui ne maîtrisent pas Threads vont rater le plus gros run organique de la décennie.'",
        ],
        warmupLink: "Compte Threads = lié à IG. Si IG est sain, Threads démarre fort."
      },
      {
        id: "engagement",
        title: "Phase 2 — Cascading Reach",
        range: "5K - 100K vues",
        intro: "Threads cascade le reach via les replies. Chaque reply boost le post original.",
        stats: [
          { label: "Reply chains", value: "≥ 3 par post" },
          { label: "Quote reposts", value: "≥ 5" },
        ],
        actions: [
          "Répondre à TOUS les replies dans la première heure",
          "Continuer la conversation = boost continuel",
          "Quote des replies intéressantes pour amplifier",
        ],
        checklist: [
          "Tous les replies < 1h répondus",
          "Quote tweet d'au moins 1 reply qualité",
        ],
        kpis: [
          { label: "Total interactions", value: "≥ 500" },
          { label: "New followers", value: "≥ 50" },
        ],
        errors: [
          "Ne pas continuer la conversation → momentum perdu",
        ],
        proTips: [],
        examples: [],
        warmupLink: ""
      },
      {
        id: "scaling",
        title: "Phase 3 — Authority Building",
        range: "100K+ vues régulières",
        intro: "Threads devient un funnel vers IG. L'objectif est de transformer le reach gratuit en croissance IG/produit.",
        stats: [],
        actions: [
          "Lien IG dans bio Threads",
          "Stratégie de 'voice' : 1-2 thèmes récurrents",
          "Newsletter ou produit linké",
        ],
        checklist: [
          "Bio Threads optimisée",
          "Pin un post manifeste (ton angle de niche)",
        ],
        kpis: [],
        errors: ["Spam liens externes → reach plomb"],
        proTips: [],
        examples: [],
        warmupLink: ""
      }
    ]
  },

  x: {
    slug: "x",
    name: "X / Twitter",
    icon: "Twitter",
    color: "#000000",
    tagline: "Arène d'opinions. Le mou meurt, le tranché explose.",
    overview: "X récompense les threads structurés et les opinions polarisantes. Premium ou non, l'algo pousse les conversations actives.",
    phases: [
      {
        id: "cold",
        title: "Phase 1 — Initial Push",
        range: "0 - 10K impressions",
        intro: "X pousse tes posts à 1-3% de tes followers. Si engagement rate ≥ 3% dans les 30 min → push à audience cold.",
        stats: [
          { label: "Engagement rate", value: "≥ 3%" },
          { label: "Replies", value: "≥ 5" },
        ],
        actions: [
          "Hook textuel : 'Hot take:', 'Personne ne te dira ça:', etc.",
          "Thread 8-12 tweets avec payoff par tweet",
          "Quote-tweet d'un post viral pour piggyback",
          "Réponses sous gros comptes < 30min",
        ],
        checklist: [
          "Premier tweet < 240 caractères",
          "Image ou vidéo dans le premier tweet",
          "Pas de lien externe avant le 3e tweet",
        ],
        kpis: [
          { label: "Impressions", value: "≥ 5K" },
          { label: "Profile visits", value: "≥ 100" },
        ],
        errors: [
          "Lien externe au premier tweet → -60% push",
          "Threads sans hook → bookmark mais pas de partage",
        ],
        proTips: [
          "Quote-tweet de gros posts dans les 5 minutes après leur publication = piggyback sur leur reach.",
        ],
        examples: [],
        warmupLink: ""
      },
      {
        id: "engagement",
        title: "Phase 2 — Conversation Push",
        range: "10K - 100K impressions",
        intro: "X push les conversations actives. Plus tu réponds dans ton thread = plus l'algo push.",
        stats: [],
        actions: [
          "Répondre à tous les replies dans l'heure",
          "Quote tes propres tweets pour relancer",
          "Bookmark = signal hot",
        ],
        checklist: [],
        kpis: [],
        errors: [],
        proTips: [],
        examples: [],
        warmupLink: ""
      },
      {
        id: "scaling",
        title: "Phase 3 — Authority + Monétisation",
        range: "100K+ régulier",
        intro: "X premium = monétisation directe via ad-revenue share + subscriptions.",
        stats: [],
        actions: ["Premium Plus pour ad-share", "Subscriptions pour audience qualifiée"],
        checklist: [],
        kpis: [],
        errors: [],
        proTips: [],
        examples: [],
        warmupLink: ""
      }
    ]
  },

  snap: {
    slug: "snap",
    name: "Snapchat",
    icon: "Ghost",
    color: "#FFFC00",
    tagline: "Spotlight = TikTok-like, audience Gen Z 13-24 ultra engagée.",
    overview: "Snap Spotlight push agressivement le contenu fun et raw. Plateforme peu saturée, opportunité massive pour 2026.",
    phases: [
      {
        id: "cold",
        title: "Phase 1 — Spotlight Test",
        range: "0 - 5K vues",
        intro: "Spotlight teste sur audience Gen Z géolocalisée. Format vertical 9:16 obligatoire.",
        stats: [
          { label: "Watchtime", value: "≥ 50%" },
          { label: "Saves", value: "≥ 1%" },
        ],
        actions: [
          "Format 9:16 plein écran",
          "Vidéo 5-15s",
          "Audio Snap natif si possible",
          "Hashtags 2-3 niche",
        ],
        checklist: [
          "Pas de watermark TT/IG",
          "Caption courte",
        ],
        kpis: [],
        errors: [
          "Watermark visible → exclusion Spotlight",
          "Format paysage → reach quasi nul",
        ],
        proTips: [
          "Snap rémunère encore certains créateurs Spotlight. Vérifier éligibilité.",
        ],
        examples: [],
        warmupLink: ""
      },
      {
        id: "scaling",
        title: "Phase 2 — Spotlight Push",
        range: "5K+ vues",
        intro: "Spotlight push géographiquement à audience large.",
        stats: [],
        actions: ["Continuité de niche", "Cross-promo Story Snap"],
        checklist: [],
        kpis: [],
        errors: [],
        proTips: [],
        examples: [],
        warmupLink: ""
      }
    ]
  },

  clapper: {
    slug: "clapper",
    name: "Clapper",
    icon: "Clapperboard",
    color: "#1DB954",
    tagline: "Réseau émergent. Audience 35-65, faible saturation, reach 5-10x supérieur.",
    overview: "Clapper favorise les early creators. Peu de concurrence + audience à fort pouvoir d'achat = goldmine 2026.",
    phases: [
      {
        id: "cold",
        title: "Phase 1 — Early Push",
        range: "0 - 10K vues",
        intro: "Clapper push agressivement les nouveaux comptes pour les retenir. Audience plus tolérante au contenu plus long.",
        stats: [],
        actions: [
          "Vidéo 15-60s",
          "Format 9:16 ou 16:9",
          "Pas de watermark TT/IG",
          "Voix off ok et même favorisée",
        ],
        checklist: [
          "Profil complet avec bio",
          "3-5 vidéos pour démarrer",
        ],
        kpis: [],
        errors: [
          "Cross-post brut TT → faible perf",
        ],
        proTips: [
          "Clapper pèse les commentaires longs et les conversations adultes. Adapter ton vs TT.",
        ],
        examples: [],
        warmupLink: ""
      },
      {
        id: "scaling",
        title: "Phase 2 — Authority",
        range: "10K+ régulier",
        intro: "Construire une audience fidèle qui converti via DM ou lien externe.",
        stats: [],
        actions: ["Lives Clapper", "DM funnel direct"],
        checklist: [],
        kpis: [],
        errors: [],
        proTips: [],
        examples: [],
        warmupLink: ""
      }
    ]
  }
};

export const ALGORITHM_LIST = Object.values(ALGORITHMS);
